import React from "react";
import StatusChip from "../../components/StatusChip.jsx";
import PopupDone, { usePopupDone } from "../../../../components/common/PopupDone/PopupDone.jsx";

const DASH = "—";

const dayLabel = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : DASH;

/* One override, read in full. Built from the view_popup_rules vocabulary
   and nothing else: .pg-modal > .pg-modal-panel--wide, a .pgm-head whose
   title is what the record is about, a .pgm-ident of the facts that place
   it, .pgm-section for the body, and a footer whose only filled button is
   Close.

   The list showed the reason clipped to whatever the column had left. The
   reason is the whole point of an override -- why a class is not where
   the timetable says it is -- so here it gets the room to be read. */
const OverrideRecordModal = ({ record, canEdit, onClose, onRevert }) => {
  // Open while it has a record; the hook runs before the early return. A revert
  // confirmed from here shows the done panel in place of the record, and
  // the popup closes itself after five seconds.
  const { done } = usePopupDone(Boolean(record));

  if (!record) return null;

  const moved = record.action === "moved";

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pgm-override-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pg-modal-panel pg-modal-panel--wide">
        {/* The batch is what the override is about, so it is the title --
            in full, never clipped the way the list column clips it. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="pgm-override-title">
              {record.batchName || DASH}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Date</dt>
                <dd>{dayLabel(record.date)}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>What happened</dt>
                <dd>
                  <StatusChip status={moved ? "shifted" : "maintenance"}>
                    {moved ? "Shifted" : "Cancelled for the day"}
                  </StatusChip>
                </dd>
              </div>
              {/* Where it normally sits, and where it went. Two facts the
                  list could only ever show one of. */}
              <div className="pgm-ident-row">
                <dt>Usual room</dt>
                <dd>{record.classroomNo || DASH}</dd>
              </div>
              {moved && (
                <div className="pgm-ident-row">
                  <dt>Moved to</dt>
                  <dd>{record.newClassroomNo || DASH}</dd>
                </div>
              )}
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">Reason</h3>
                {record.reason ? (
                  <p className="lp-plain">{record.reason}</p>
                ) : (
                  <p className="pgm-empty">No reason was recorded.</p>
                )}
              </section>
            </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end.
            Close stays last and is the only filled button, so the
            destructive one is never the default under the cursor. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              {canEdit && (
                <button
                  type="button"
                  className="pgm-btn pgm-btn--danger"
                  onClick={() => onRevert(record)}
                >
                  Revert override
                </button>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default OverrideRecordModal;
